<template>
  <div class="profile-general">

    <!-- =====================================================
         IDENTITY / COVER
    ====================================================== -->

    <section class="identity-card">

      <div
        class="cover"
        :style="{ backgroundImage: coverBg }"
      >
        <div
          v-if="!coverLoaded"
          class="cover-skeleton"
        ></div>

        <div class="cover-overlay"></div>


        <button
          type="button"
          class="edit-cover-btn"
          :disabled="isUploadingCover"
          @click="coverInputRef?.click()"
        >
          <span
            v-if="isUploadingCover"
            class="cover-spinner"
          ></span>

          <svg
            v-else
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M13.8 3.8L16.2 6.2L7 15.4L3.8 16.2L4.6 13L13.8 3.8Z"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linejoin="round"
            />
          </svg>

          <span>
            Изменить обложку
          </span>
        </button>


        <input
          ref="coverInputRef"
          type="file"
          accept="image/*"
          hidden
          @change="onCoverChange"
        />
      </div>


      <div class="identity-body">

        <div class="avatar-wrapper">

          <button
            type="button"
            class="avatar-button"
            :disabled="isAvatarLoading"
            @click="avatarInputRef?.click()"
          >
            <img
              :key="avatarKey"
              :src="
                avatarPreviewUrl
                || props.user?.image_url
                || defaultAvatar
              "
              class="avatar"
              alt=""
            />


            <div class="avatar-hover">
              <svg
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M13.8 3.8L16.2 6.2L7 15.4L3.8 16.2L4.6 13L13.8 3.8Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linejoin="round"
                />
              </svg>
            </div>


            <div
              v-if="isAvatarLoading"
              class="avatar-loader"
            >
              <span class="avatar-spinner"></span>
            </div>

          </button>


          <input
            ref="avatarInputRef"
            type="file"
            accept="image/*"
            hidden
            @change="onAvatarChange"
          />

        </div>


        <div class="identity-copy">

          <div class="eyebrow">

          </div>

          <h2 class="user-name">
            {{ form.name || props.user?.name || "Без имени" }}
          </h2>


          <button
            type="button"
            class="location-button"
            @click="openRegionDrawer"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M10 17C10 17 15 12.7 15 8.5C15 5.46 12.76 3.5 10 3.5C7.24 3.5 5 5.46 5 8.5C5 12.7 10 17 10 17Z"
                stroke="currentColor"
                stroke-width="1.45"
              />

              <circle
                cx="10"
                cy="8.5"
                r="1.7"
                stroke="currentColor"
                stroke-width="1.35"
              />
            </svg>


            <span>
              {{
                selectedRegion?.name
                || props.user?.city?.name
                || "Указать город"
              }}
            </span>


            <span
              v-if="
                selectedRegion?.region_name
                || props.user?.city?.region_name
              "
              class="region-name"
            >
              ·
              {{
                selectedRegion?.region_name
                || props.user?.city?.region_name
              }}
            </span>


            <span class="location-chevron">
              ›
            </span>
          </button>

        </div>

      </div>

    </section>


    <!-- =====================================================
         PERSONAL INFO
    ====================================================== -->

    <section class="general-card details-card">

      <div class="card-heading">

        <div class="heading-icon personal-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="8"
              r="3"
              stroke="currentColor"
              stroke-width="1.6"
            />

            <path
              d="M6 19C6.7 15.8 8.7 14 12 14C15.3 14 17.3 15.8 18 19"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />
          </svg>
        </div>


        <div>
          <div class="section-eyebrow">
            Основные данные
          </div>

          <h3>
            Личная информация
          </h3>

          <p>
            Основные данные, которые используются в вашем профиле.
          </p>
        </div>

      </div>


      <div class="form-grid">

        <label class="form-field">
          <span class="field-label">
            Имя и фамилия
          </span>

          <input
            v-model="form.name"
            type="text"
            placeholder="Ваше имя"
          />
        </label>


        <label class="form-field">
          <span class="field-label">
            Возраст
          </span>

          <input
            v-model.number="form.age"
            type="number"
            min="0"
            max="120"
            placeholder="Например, 24"
          />
        </label>


        <label class="form-field">
          <span class="field-label">
            Специфика
          </span>

          <input
            v-model="form.specifics"
            type="text"
            placeholder="Например, iOS-разработчик"
          />
        </label>


        <label class="form-field">
          <span class="field-label">
            Номер телефона
          </span>

          <input
            ref="phoneInputRef"
            v-model="form.phone_number"
            type="tel"
            placeholder="+7 (999) 000-00-00"
          />
        </label>


        <label class="form-field form-field--wide">
          <span class="field-label">
            Email
          </span>

          <input
            v-model="form.email"
            type="email"
            placeholder="Введите ваш e-mail"
          />
        </label>


        <label class="form-field form-field--wide">
          <span class="field-label">
            О себе
          </span>

          <textarea
            v-model="form.about_self"
            placeholder="Расскажите о своём опыте, специализации и интересах"
          ></textarea>
        </label>

      </div>

    </section>


    <!-- =====================================================
         STATUS
    ====================================================== -->

    <section class="general-card status-card">

      <div class="section-eyebrow status-eyebrow">
        Профессиональный статус
      </div>


      <button
        type="button"
        class="status-row"
        @click="showStatusDrawer = true"
      >

        <div class="status-icon">
          <span class="status-dot"></span>
        </div>


        <div class="status-copy">

          <div class="status-caption">
            Текущий статус
          </div>

          <div class="status-value">
            {{ currentStatusLabel }}
          </div>

        </div>


        <div class="status-chevron">
          <svg
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M8 6L12 10L8 14"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

      </button>

    </section>


    <!-- =====================================================
         WORK / PRIVACY
    ====================================================== -->

    <section class="general-card preferences-card">

      <div class="card-heading">

        <div class="heading-icon preferences-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <rect
              x="4"
              y="6"
              width="16"
              height="12"
              rx="3"
              stroke="currentColor"
              stroke-width="1.6"
            />

            <circle
              cx="12"
              cy="12"
              r="2.5"
              stroke="currentColor"
              stroke-width="1.5"
            />
          </svg>
        </div>


        <div>
          <div class="section-eyebrow">
            Предпочтения
          </div>

          <h3>
            Работа и приватность
          </h3>

          <p>
            Настройте зарплатные ожидания и видимость контактных данных.
          </p>
        </div>

      </div>


      <!-- SALARY -->

      <div class="salary-block">

        <div class="field-label">
          Минимальная зарплата
        </div>


        <div class="salary-wrapper">

          <input
            v-model="form.salary"
            type="text"
            class="salary-input"
            placeholder="Например, 180000"
            @input="
              form.salary =
                String(form.salary || '')
                  .replace(/\D/g, '')
            "
          />


          <div
            class="currency-wrapper"
            @click.stop="toggleCurrencyMenu"
          >

            <span>
              {{ form.salary_currency }}
            </span>


            <svg
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5 6L8 9L11 6"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>


            <transition name="slide-fade">

              <ul
                v-if="showCurrencyMenu"
                class="currency-dropdown"
              >
                <li
                  v-for="currency in currencies"
                  :key="currency"
                  @click.stop="changeCurrency(currency)"
                >
                  {{ currency }}
                </li>
              </ul>

            </transition>

          </div>

        </div>

      </div>


      <!-- SETTINGS -->

      <div class="settings-list">

        <label class="setting-row">

          <div class="setting-icon privacy-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M7.5 10V8C7.5 5.5 9.3 4 12 4C14.7 4 16.5 5.5 16.5 8V10"
                stroke="currentColor"
                stroke-width="1.6"
              />

              <rect
                x="5.5"
                y="10"
                width="13"
                height="9"
                rx="2.5"
                stroke="currentColor"
                stroke-width="1.6"
              />
            </svg>
          </div>


          <div class="setting-copy">

            <div class="setting-title">
              Скрыть номер телефона
            </div>

            <div class="setting-description">
              Другие пользователи не увидят ваш номер в профиле.
            </div>

          </div>


          <input
            v-model="form.is_phone_number_hidden"
            type="checkbox"
          />

          <span class="ios-switch"></span>

        </label>


        <label class="setting-row">

          <div class="setting-icon remote-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5 11L12 5L19 11V18H14.5V14H9.5V18H5V11Z"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
            </svg>
          </div>


          <div class="setting-copy">

            <div class="setting-title">
              Ищу только удалённую работу
            </div>

            <div class="setting-description">
              В рекомендациях будут учитываться удалённые вакансии.
            </div>

          </div>


          <input
            v-model="form.is_remote"
            type="checkbox"
          />

          <span class="ios-switch"></span>

        </label>

      </div>


      <!-- =====================================================
           SAVE
      ====================================================== -->

      <div class="save-area">

        <div class="save-state">

          <span
            class="save-state-dot"
            :class="{ changed: hasChanges }"
          ></span>

          {{
            hasChanges
              ? "Есть несохранённые изменения"
              : "Все изменения сохранены"
          }}

        </div>


        <button
          type="button"
          class="save-btn"
          :class="{
            active: hasChanges && !isSaving,
            saving: isSaving
          }"
          :disabled="!hasChanges || isSaving"
          @click="onSave"
        >

          <span
            v-if="isSaving"
            class="button-spinner"
          ></span>

          <span>
            {{ isSaving ? "Сохраняем…" : "Сохранить" }}
          </span>

        </button>

      </div>

    </section>


    <!-- =====================================================
         DRAWERS
    ====================================================== -->

    <ProfileRegionDrawer
      v-if="showRegionDrawer"
      :visible="showRegionDrawer"
      :regions="regionList"
      :model-value="selectedRegion"
      @update:model-value="handleRegionChange"
      @close="showRegionDrawer = false"
    />


    <ProfileHashtagsDrawer
      :visible="showHashtagDrawer"
      :initial-hashtags="props.user?.hashtags || []"
      @close="showHashtagDrawer = false"
    />


    <StatusDrawer
      :visible="showStatusDrawer"
      v-model:model-value="form.job_status"
      @close="showStatusDrawer = false"
      @apply="showStatusDrawer = false"
    />


    <ErrorToast
      v-if="showError"
      :message="errorMessage"
    />

  </div>
</template>


<script setup>

import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from "vue";

import IMask from "imask";

import UserService
  from "../../api/UserService";

import ErrorToast
  from "../../components/ErrorToast.vue";

import ProfileHashtagsDrawer
  from "./ProfileHashtagsDrawer.vue";

import ProfileRegionDrawer
  from "./ProfileRegionDrawer.vue";

import StatusDrawer
  from "./StatusDrawer.vue";

import defaultAvatar
  from "../../assets/default-avatar.png";


/* ============================================================
   PROPS / EMITS
============================================================ */

const props = defineProps({

  user: {
    type: Object,
    default: () => ({})
  },

  regionList: {
    type: Array,
    default: () => []
  }

});


const emit = defineEmits([
  "update:form",
  "avatarChanged",
  "coverChanged",
  "updated"
]);


/* ============================================================
   FORM
============================================================ */

const form = ref({

  name: "",
  age: null,

  specifics: "",

  phone_number: "",
  email: "",

  about_self: "",

  job_status: 1,

  is_phone_number_hidden: false,
  is_remote: false,

  salary: "",
  salary_currency: "₽"

});


const originalUser =
  ref({});


const initializing =
  ref(true);


const hasChanges =
  ref(false);


const isSaving =
  ref(false);


/* ============================================================
   AVATAR / COVER
============================================================ */

const avatarInputRef =
  ref(null);

const coverInputRef =
  ref(null);

const phoneInputRef =
  ref(null);


const avatarPreviewUrl =
  ref(null);

const avatarKey =
  ref(0);


const isAvatarLoading =
  ref(false);

const isUploadingCover =
  ref(false);


const coverLoaded =
  ref(false);

const coverBg =
  ref("");


const gradients = [

  "linear-gradient(135deg, #7B61FF, #C972E6)",

  "linear-gradient(135deg, #2173E5, #204F90)",

  "linear-gradient(135deg, #57D76C, #27B782)",

  "linear-gradient(135deg, #FF8C42, #FF3C3C)"

];


/* ============================================================
   UI STATE
============================================================ */

const showStatusDrawer =
  ref(false);

const showRegionDrawer =
  ref(false);

const showHashtagDrawer =
  ref(false);

const showCurrencyMenu =
  ref(false);


const showError =
  ref(false);

const errorMessage =
  ref("");


const currencies = [
  "₽",
  "$",
  "€"
];


const selectedRegion =
  ref(
    props.user?.city || null
  );


let phoneMask =
  null;


/* ============================================================
   STATUS
============================================================ */

const currentStatusValue =
  computed(() => {

    return (
      form.value?.job_status
      ?? props.user?.job_status
      ?? null
    );

  });


const currentStatusLabel =
  computed(() => {

    switch (
      Number(
        currentStatusValue.value
      )
    ) {

      case 1:
        return "В активном поиске работы";

      case 2:
        return "В неспешном поиске работы";

      case 3:
        return "Только нетворкинг";

      case 4:
        return "Тут только для рекрутинга";

      default:
        return "Статус не указан";

    }

  });


/* ============================================================
   EDITABLE SNAPSHOT
============================================================ */

function getEditableSnapshot(
  value = {}
) {

  return {

    name:
      value.name || "",

    age:
      value.age ?? null,

    specifics:
      value.specifics || "",

    phone_number:
      value.phone_number || "",

    email:
      value.email || "",

    about_self:
      value.about_self || "",

    job_status:
      value.job_status ?? 1,

    salary:
      value.salary ?? "",

    salary_currency:
      value.salary_currency || "₽",

    is_remote:
      Boolean(
        value.is_remote
      ),

    is_phone_number_hidden:
      Boolean(
        value.is_phone_number_hidden
      ),

    city:
      value.city || null

  };

}


/* ============================================================
   APPLY USER
============================================================ */

function applyUser(
  user
) {

  if (!user) {
    return;
  }


  initializing.value =
    true;


  const snapshot =
    getEditableSnapshot(
      user
    );


  form.value = {
    ...form.value,
    ...snapshot
  };


  selectedRegion.value =
    user.city || null;


  originalUser.value =
    getEditableSnapshot({
      ...snapshot,
      city:
        user.city || null
    });


  avatarPreviewUrl.value =
    null;


  hasChanges.value =
    false;


  setCover();


  requestAnimationFrame(() => {

    initializing.value =
      false;

  });

}


/* ============================================================
   WATCH USER
============================================================ */

watch(
  () => props.user,

  value => {

    if (!value) {
      return;
    }


    applyUser(
      value
    );

  },

  {
    deep: true,
    immediate: true
  }
);


/* ============================================================
   CHANGES
============================================================ */

watch(
  [
    form,
    selectedRegion
  ],

  () => {

    if (
      initializing.value
    ) {
      return;
    }


    const current =
      getEditableSnapshot({
        ...form.value,
        city:
          selectedRegion.value
      });


    hasChanges.value =
      JSON.stringify(
        current
      )
      !==
      JSON.stringify(
        originalUser.value
      );


    emit(
      "update:form",
      current
    );

  },

  {
    deep: true
  }
);


/* ============================================================
   COVER
============================================================ */

function setCover() {

  const url =
    props.user?.cover_url;


  if (!url) {

    coverBg.value =
      pickRandomGradient();

    coverLoaded.value =
      true;

    return;

  }


  coverLoaded.value =
    false;


  const img =
    new Image();


  img.onload =
    () => {

      coverBg.value =
        `url("${url}")`;

      coverLoaded.value =
        true;

    };


  img.onerror =
    () => {

      coverBg.value =
        pickRandomGradient();

      coverLoaded.value =
        true;

    };


  img.src =
    url;

}


function pickRandomGradient() {

  return gradients[
    Math.floor(
      Math.random()
      * gradients.length
    )
  ];

}


/* ============================================================
   REGION
============================================================ */

function openRegionDrawer() {

  showRegionDrawer.value =
    true;

}


function handleRegionChange(
  region
) {

  selectedRegion.value =
    region;

}


/* ============================================================
   CURRENCY
============================================================ */

function toggleCurrencyMenu() {

  showCurrencyMenu.value =
    !showCurrencyMenu.value;

}


function changeCurrency(
  currency
) {

  form.value.salary_currency =
    currency;

  showCurrencyMenu.value =
    false;

}


function handleDocumentClick() {

  showCurrencyMenu.value =
    false;

}


/* ============================================================
   SAVE
============================================================ */

async function onSave() {

  if (
    isSaving.value
    || !hasChanges.value
  ) {
    return;
  }


  isSaving.value =
    true;


  try {

    const payload = {

      name:
        form.value.name?.trim()
        || null,

      age:
        form.value.age === ""
        || form.value.age === null
        || form.value.age === undefined
          ? null
          : Number(
              form.value.age
            ),

      specifics:
        form.value.specifics?.trim()
        || null,

      phone_number:
        form.value.phone_number
          ?.replace(
            /\s|\(|\)|-/g,
            ""
          )
        || null,

      email:
        form.value.email?.trim()
        || null,

      about_self:
        form.value.about_self?.trim()
        || null,

      job_status:
        Number(
          form.value.job_status
        ) || 1,

      is_phone_number_hidden:
        Boolean(
          form.value
            .is_phone_number_hidden
        ),

      is_remote:
        Boolean(
          form.value.is_remote
        ),

      salary:
        form.value.salary
          ? Number(
              String(
                form.value.salary
              ).replace(
                /[^\d]/g,
                ""
              )
            )
          : null,

      salary_currency:
        form.value.salary_currency
        || "₽",

      city:
        selectedRegion.value
        || null

    };


    await UserService
      .updateUserProfile(
        payload
      );


    const normalizedCurrent =
      getEditableSnapshot({
        ...form.value,
        ...payload,
        city:
          selectedRegion.value
      });


    originalUser.value =
      normalizedCurrent;


    hasChanges.value =
      false;


    emit(
      "updated",
      payload
    );


  } catch (error) {

    console.error(
      "❌ Ошибка при сохранении:",
      error
    );


    errorMessage.value =
      error?.response?.data?.message
      || error?.message
      || "Произошла ошибка при сохранении";


    showError.value =
      true;


    setTimeout(() => {

      showError.value =
        false;

    }, 3000);


  } finally {

    isSaving.value =
      false;

  }

}


/* ============================================================
   AVATAR
============================================================ */

async function onAvatarChange(
  event
) {

  const file =
    event.target.files?.[0];


  if (!file) {
    return;
  }


  isAvatarLoading.value =
    true;


  try {

    const result =
      await UserService
        .uploadUserAvatar(
          file,
          true
        );


    if (result?.url) {

      const freshUrl =
        `${result.url}?v=${Date.now()}`;


      avatarPreviewUrl.value =
        freshUrl;


      avatarKey.value +=
        1;


      form.value.image_url =
        freshUrl;


      emit(
        "avatarChanged",
        freshUrl
      );


      emit(
        "updated",
        {
          image_url:
            freshUrl
        }
      );

    }


  } catch (error) {

    console.error(
      "❌ Ошибка при загрузке аватара:",
      error
    );


    errorMessage.value =
      "Ошибка при загрузке аватара";


    showError.value =
      true;


    setTimeout(() => {

      showError.value =
        false;

    }, 3000);


  } finally {

    isAvatarLoading.value =
      false;


    event.target.value =
      "";

  }

}


/* ============================================================
   COVER UPLOAD
============================================================ */

async function onCoverChange(
  event
) {

  const file =
    event.target.files?.[0];


  if (!file) {
    return;
  }


  isUploadingCover.value =
    true;


  try {

    const result =
      await UserService
        .uploadUserCover(
          file,
          true
        );


    if (result?.url) {

      const freshUrl =
        `${result.url}?v=${Date.now()}`;


      coverBg.value =
        `url("${freshUrl}")`;


      coverLoaded.value =
        true;


      emit(
        "coverChanged",
        freshUrl
      );


      emit(
        "updated",
        {
          cover_url:
            freshUrl
        }
      );

    }


  } catch (error) {

    console.error(
      "❌ Ошибка при загрузке обложки:",
      error
    );


    errorMessage.value =
      "Ошибка при загрузке обложки";


    showError.value =
      true;


    setTimeout(() => {

      showError.value =
        false;

    }, 3000);


  } finally {

    isUploadingCover.value =
      false;


    event.target.value =
      "";

  }

}


/* ============================================================
   MOUNT
============================================================ */

onMounted(() => {

  document.addEventListener(
    "click",
    handleDocumentClick
  );


  if (
    phoneInputRef.value
  ) {

    phoneMask =
      IMask(
        phoneInputRef.value,
        {

          mask: [

            {
              mask:
                "+{7} (000) 000-00-00",

              startsWith:
                "7",

              lazy:
                false
            },

            {
              mask:
                "+{1} (000) 000-0000",

              startsWith:
                "1",

              lazy:
                false
            },

            {
              mask:
                "+000000000000",

              lazy:
                false
            }

          ],


          dispatch(
            appended,
            dynamicMasked
          ) {

            const number =
              (
                dynamicMasked.value
                + appended
              )
                .replace(
                  /\D/g,
                  ""
                );


            return (
              dynamicMasked
                .compiledMasks
                .find(
                  mask =>
                    mask.startsWith
                    && number.startsWith(
                      mask.startsWith
                    )
                )
              || dynamicMasked
                .compiledMasks[2]
            );

          }

        }
      );

  }

});


onBeforeUnmount(() => {

  document.removeEventListener(
    "click",
    handleDocumentClick
  );


  if (
    phoneMask
  ) {

    phoneMask.destroy();

    phoneMask =
      null;

  }

});

</script>


<style scoped>

/* ============================================================
   ROOT
============================================================ */

.profile-general {
  width: 100%;

  display: flex;
  flex-direction: column;

  gap: 16px;

  font-family:
    "Eastman-Regular",
    sans-serif;
}


/* ============================================================
   CARD
============================================================ */

.identity-card,
.general-card {
  position: relative;

  width: 100%;

  box-sizing: border-box;

  overflow: hidden;

  background: #ffffff;

  border:
    1px solid
    rgba(20, 35, 60, 0.09);

  border-radius: 24px;

  box-shadow:
    0 16px 45px
    rgba(15, 23, 42, 0.055);
}


/* ============================================================
   COVER
============================================================ */

.cover {
  position: relative;

  width: 100%;
  height: 225px;

  overflow: hidden;

  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}


.cover-overlay {
  position: absolute;

  inset: 0;

  background:
    linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.01),
      rgba(0, 0, 0, 0.18)
    );

  pointer-events: none;
}


.cover-skeleton {
  position: absolute;

  inset: 0;

  z-index: 5;

  overflow: hidden;

  background: #e9edf3;
}


.cover-skeleton::after {
  content: "";

  position: absolute;

  inset: 0;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255,255,255,0.72),
      transparent
    );

  transform:
    translateX(-100%);

  animation:
    coverShimmer
    1.3s
    infinite;
}


@keyframes coverShimmer {

  to {
    transform:
      translateX(100%);
  }

}


/* ============================================================
   COVER BUTTON
============================================================ */

.edit-cover-btn {
  position: absolute;

  right: 18px;
  bottom: 18px;

  z-index: 10;

  min-height: 39px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 7px;

  padding:
    0
    13px;

  color: #ffffff;

  background:
    rgba(20, 24, 31, 0.48);

  border:
    1px solid
    rgba(255,255,255,0.25);

  border-radius: 12px;

  backdrop-filter:
    blur(14px);

  -webkit-backdrop-filter:
    blur(14px);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  cursor: pointer;

  transition:
    transform 0.2s ease,
    background-color 0.2s ease;
}


.edit-cover-btn svg {
  width: 15px;
  height: 15px;
}


.edit-cover-btn:hover:not(:disabled) {
  transform:
    translateY(-1px);

  background:
    rgba(20, 24, 31, 0.62);
}


.edit-cover-btn:disabled {
  opacity: 0.62;

  cursor: default;
}


.cover-spinner {
  width: 13px;
  height: 13px;

  border:
    2px solid
    rgba(255,255,255,0.35);

  border-top-color:
    #ffffff;

  border-radius: 50%;

  animation:
    spin
    0.7s
    linear
    infinite;
}


/* ============================================================
   IDENTITY
============================================================ */

.identity-body {
  display: flex;
  align-items: flex-end;

  gap: 17px;

  padding:
    0
    28px
    25px;
}


.avatar-wrapper {
  position: relative;

  z-index: 15;

  flex-shrink: 0;

  margin-top:
    -52px;
}


.avatar-button {
  position: relative;

  width: 112px;
  height: 112px;

  display: block;

  padding: 0;

  overflow: hidden;

  background: #edf0f4;

  border:
    5px solid
    #ffffff;

  border-radius: 50%;

  box-shadow:
    0 14px 34px
    rgba(15, 23, 42, 0.16);

  cursor: pointer;
}


.avatar {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;
}


.avatar-hover {
  position: absolute;

  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;

  background:
    rgba(15, 20, 28, 0.44);

  opacity: 0;

  transition:
    opacity
    0.2s ease;
}


.avatar-hover svg {
  width: 20px;
  height: 20px;
}


.avatar-button:hover
.avatar-hover {
  opacity: 1;
}


.avatar-loader {
  position: absolute;

  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  background:
    rgba(255,255,255,0.72);

  backdrop-filter:
    blur(4px);
}


.avatar-spinner {
  width: 24px;
  height: 24px;

  border:
    3px solid
    rgba(40,135,247,0.17);

  border-top-color:
    #2887f7;

  border-radius: 50%;

  animation:
    spin
    0.7s
    linear
    infinite;
}


@keyframes spin {

  to {
    transform:
      rotate(360deg);
  }

}


.identity-copy {
  min-width: 0;

  flex: 1;

  padding-bottom: 4px;
}


.eyebrow,
.section-eyebrow {
  margin-bottom: 5px;

  color: #979ca5;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  line-height: 1.2;

  letter-spacing: 0.07em;

  text-transform: uppercase;
}


.user-name {
  margin: 0;

  overflow-wrap: anywhere;

  color: #272c33;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 19px;

  line-height: 1.25;

  letter-spacing: -0.3px;
}


/* ============================================================
   LOCATION
============================================================ */

.location-button {
  width: fit-content;

  max-width: 100%;

  display: flex;
  align-items: center;

  gap: 6px;

  margin-top: 9px;

  padding:
    7px
    9px;

  color: #67717e;

  background:
    rgba(45, 115, 220, 0.055);

  border:
    1px solid
    rgba(45, 115, 220, 0.08);

  border-radius: 10px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  cursor: pointer;

  transition:
    background-color
    0.2s ease,
    transform
    0.2s ease;
}


.location-button:hover {
  background:
    rgba(45, 115, 220, 0.09);

  transform:
    translateY(-1px);
}


.location-button svg {
  width: 14px;
  height: 14px;

  flex-shrink: 0;

  color: #477ed7;
}


.region-name {
  color: #989da5;
}


.location-chevron {
  margin-left: 2px;

  color: #477ed7;

  font-size: 14px;

  line-height: 1;
}


/* ============================================================
   GENERAL CARDS
============================================================ */

.general-card {
  padding:
    26px
    28px;
}


.card-heading {
  display: flex;
  align-items: flex-start;

  gap: 13px;

  margin-bottom: 22px;
}


.heading-icon {
  width: 42px;
  height: 42px;

  flex:
    0
    0
    42px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 13px;
}


.heading-icon svg {
  width: 19px;
  height: 19px;
}


.personal-icon {
  color: #477ed7;

  background:
    rgba(58, 121, 220, 0.075);
}


.preferences-icon {
  color: #735fd1;

  background:
    rgba(112, 89, 209, 0.075);
}


.card-heading h3 {
  margin: 0;

  color: #30353d;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 14px;

  font-weight: 500;
}


.card-heading p {
  margin:
    5px
    0
    0;

  color: #989da5;

  font-size: 9px;

  line-height: 1.4;
}


/* ============================================================
   FORM
============================================================ */

.form-grid {
  display: grid;

  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );

  gap:
    17px
    14px;
}


.form-field {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 7px;
}


.form-field--wide {
  grid-column:
    1 / -1;
}


.field-label {
  color: #666d77;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
}


.form-field input,
.form-field textarea {
  width: 100%;

  box-sizing: border-box;

  color: #30353d;

  background: #f7f8fa;

  border:
    1px solid
    #e2e6eb;

  outline: none;

  border-radius: 12px;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;

  transition:
    background-color
    0.2s ease,
    border-color
    0.2s ease,
    box-shadow
    0.2s ease;
}


.form-field input {
  height: 44px;

  padding:
    0
    13px;
}


.form-field textarea {
  min-height: 125px;

  padding:
    12px
    13px;

  resize: vertical;

  line-height: 1.5;
}


.form-field input:focus,
.form-field textarea:focus {
  background: #ffffff;

  border-color: #5c8ff7;

  box-shadow:
    0
    0
    0
    3px
    rgba(92,143,247,0.1);
}


.form-field input::placeholder,
.form-field textarea::placeholder {
  color: #a9aeb6;
}


/* ============================================================
   STATUS
============================================================ */

.status-card {
  padding:
    24px
    28px;
}


.status-eyebrow {
  margin-bottom: 11px;
}


.status-row {
  width: 100%;

  display: flex;
  align-items: center;

  gap: 12px;

  padding: 13px;

  text-align: left;

  color: inherit;

  background:
    linear-gradient(
      145deg,
      rgba(40,135,247,0.055),
      rgba(124,92,231,0.035)
    );

  border:
    1px solid
    rgba(55,115,210,0.1);

  border-radius: 14px;

  cursor: pointer;

  transition:
    transform
    0.2s ease,
    border-color
    0.2s ease,
    box-shadow
    0.2s ease;
}


.status-row:hover {
  transform:
    translateY(-1px);

  border-color:
    rgba(55,115,210,0.18);

  box-shadow:
    0
    8px
    20px
    rgba(35,75,140,0.055);
}


.status-icon {
  width: 35px;
  height: 35px;

  flex:
    0
    0
    35px;

  display: flex;
  align-items: center;
  justify-content: center;

  background:
    rgba(58,184,116,0.08);

  border-radius: 10px;
}


.status-dot {
  width: 8px;
  height: 8px;

  background: #3ab874;

  border-radius: 50%;

  box-shadow:
    0
    0
    0
    4px
    rgba(58,184,116,0.09);
}


.status-copy {
  min-width: 0;

  flex: 1;
}


.status-caption {
  color: #969ba3;

  font-size: 7px;
}


.status-value {
  margin-top: 3px;

  color: #343940;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;
}


.status-chevron {
  width: 28px;
  height: 28px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #6283b7;

  background:
    rgba(60,110,190,0.055);

  border-radius: 8px;
}


.status-chevron svg {
  width: 15px;
  height: 15px;
}


/* ============================================================
   SALARY
============================================================ */

.salary-block {
  padding-bottom: 20px;

  border-bottom:
    1px solid
    rgba(25,40,60,0.06);
}


.salary-wrapper {
  min-height: 45px;

  display: flex;
  align-items: center;

  margin-top: 8px;

  padding:
    0
    8px
    0
    13px;

  box-sizing: border-box;

  background: #f7f8fa;

  border:
    1px solid
    #e2e6eb;

  border-radius: 12px;

  transition:
    background-color
    0.2s ease,
    border-color
    0.2s ease,
    box-shadow
    0.2s ease;
}


.salary-wrapper:focus-within {
  background: #ffffff;

  border-color: #5c8ff7;

  box-shadow:
    0
    0
    0
    3px
    rgba(92,143,247,0.1);
}


.salary-input {
  min-width: 0;

  flex: 1;

  padding: 0;

  color: #30353d;

  background: transparent;

  border: none;
  outline: none;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;
}


.salary-input::placeholder {
  color: #a9aeb6;
}


.currency-wrapper {
  position: relative;

  min-width: 52px;
  height: 32px;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 4px;

  margin-left: 9px;

  color: #3978d8;

  background:
    rgba(40,135,247,0.075);

  border:
    1px solid
    rgba(40,135,247,0.1);

  border-radius: 9px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  cursor: pointer;
}


.currency-wrapper svg {
  width: 11px;
  height: 11px;
}


.currency-dropdown {
  position: absolute;

  top:
    calc(100% + 7px);

  right: 0;

  z-index: 30;

  min-width: 60px;

  margin: 0;
  padding: 5px;

  list-style: none;

  background: #ffffff;

  border:
    1px solid
    #e2e5ea;

  border-radius: 10px;

  box-shadow:
    0
    12px
    30px
    rgba(15,23,42,0.12);
}


.currency-dropdown li {
  min-height: 30px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #4e545d;

  border-radius: 7px;

  font-size: 10px;

  cursor: pointer;
}


.currency-dropdown li:hover {
  color: #3174d8;

  background:
    rgba(40,135,247,0.07);
}


/* ============================================================
   SETTINGS
============================================================ */

.settings-list {
  display: flex;
  flex-direction: column;

  margin-top: 15px;
}


.setting-row {
  min-height: 67px;

  display: grid;

  grid-template-columns:
    38px
    minmax(0, 1fr)
    46px;

  align-items: center;

  gap: 11px;

  padding:
    10px
    4px;

  box-sizing: border-box;

  cursor: pointer;
}


.setting-row + .setting-row {
  border-top:
    1px solid
    rgba(25,40,60,0.055);
}


.setting-icon {
  width: 38px;
  height: 38px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 11px;
}


.setting-icon svg {
  width: 18px;
  height: 18px;
}


.privacy-icon {
  color: #6882ad;

  background:
    rgba(70,105,160,0.065);
}


.remote-icon {
  color: #37966f;

  background:
    rgba(55,155,115,0.07);
}


.setting-copy {
  min-width: 0;
}


.setting-title {
  color: #464c55;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
}


.setting-description {
  margin-top: 4px;

  color: #9a9fa7;

  font-size: 7px;

  line-height: 1.4;
}


.setting-row input {
  display: none;
}


/* ============================================================
   IOS SWITCH
============================================================ */

.ios-switch {
  position: relative;

  width: 46px;
  height: 26px;

  background: #d9dde3;

  border-radius: 999px;

  transition:
    background-color
    0.25s ease,
    box-shadow
    0.25s ease;
}


.ios-switch::before {
  content: "";

  position: absolute;

  top: 3px;
  left: 3px;

  width: 20px;
  height: 20px;

  background: #ffffff;

  border-radius: 50%;

  box-shadow:
    0
    2px
    7px
    rgba(15,23,42,0.18);

  transition:
    transform
    0.25s
    cubic-bezier(0.22,1,0.36,1);
}


.setting-row
input:checked
+
.ios-switch {
  background: #34c759;

  box-shadow:
    0
    4px
    12px
    rgba(52,199,89,0.22);
}


.setting-row
input:checked
+
.ios-switch::before {
  transform:
    translateX(20px);
}


/* ============================================================
   SAVE
============================================================ */

.save-area {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 20px;

  margin-top: 16px;

  padding-top: 17px;

  border-top:
    1px solid
    rgba(25,40,60,0.065);
}


.save-state {
  display: inline-flex;
  align-items: center;

  gap: 6px;

  color: #9a9fa7;

  font-size: 8px;
}


.save-state-dot {
  width: 5px;
  height: 5px;

  background: #b9bdc3;

  border-radius: 50%;
}


.save-state-dot.changed {
  background: #f2a23a;
}


.save-btn {
  min-width: 128px;
  min-height: 40px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  padding:
    0
    16px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3187ee,
      #4968e6
    );

  border: none;
  border-radius: 12px;

  box-shadow:
    0
    7px
    17px
    rgba(40,111,225,0.2);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  cursor: pointer;

  opacity: 0.4;

  transition:
    opacity
    0.2s ease,
    transform
    0.2s ease,
    box-shadow
    0.2s ease;
}


.save-btn.active {
  opacity: 1;
}


.save-btn:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0
    10px
    22px
    rgba(40,111,225,0.27);
}


.save-btn:disabled {
  cursor: default;

  box-shadow: none;
}


.button-spinner {
  width: 14px;
  height: 14px;

  border:
    2px solid
    rgba(255,255,255,0.35);

  border-top-color:
    #ffffff;

  border-radius: 50%;

  animation:
    spin
    0.65s
    linear
    infinite;
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.identity-card,
html[data-theme="dark"]
.general-card {
  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(255,255,255,0.07);

  box-shadow:
    0
    18px
    50px
    rgba(0,0,0,0.24);
}


html[data-theme="dark"]
.avatar-button {
  border-color: #22242a;
}


html[data-theme="dark"]
.avatar-loader {
  background:
    rgba(28,30,34,0.76);
}


html[data-theme="dark"]
.user-name,
html[data-theme="dark"]
.card-heading h3,
html[data-theme="dark"]
.status-value {
  color: #f0f1f3;
}


html[data-theme="dark"]
.location-button {
  color: #a6adb7;

  background:
    rgba(80,135,225,0.08);

  border-color:
    rgba(90,145,235,0.1);
}


html[data-theme="dark"]
.form-field input,
html[data-theme="dark"]
.form-field textarea,
html[data-theme="dark"]
.salary-wrapper {
  color: #f0f1f3;

  background: #292b30;

  border-color: #3b3e45;
}


html[data-theme="dark"]
.form-field input:focus,
html[data-theme="dark"]
.form-field textarea:focus,
html[data-theme="dark"]
.salary-wrapper:focus-within {
  background: #2e3036;

  border-color: #588bec;

  box-shadow:
    0
    0
    0
    3px
    rgba(88,139,236,0.13);
}


html[data-theme="dark"]
.form-field input::placeholder,
html[data-theme="dark"]
.form-field textarea::placeholder,
html[data-theme="dark"]
.salary-input::placeholder {
  color: #737780;
}


html[data-theme="dark"]
.field-label {
  color: #b7bbc2;
}


html[data-theme="dark"]
.status-row {
  background:
    linear-gradient(
      145deg,
      rgba(70,125,220,0.09),
      rgba(110,90,220,0.055)
    );

  border-color:
    rgba(100,145,230,0.12);
}


html[data-theme="dark"]
.salary-block,
html[data-theme="dark"]
.setting-row + .setting-row,
html[data-theme="dark"]
.save-area {
  border-color:
    rgba(255,255,255,0.06);
}


html[data-theme="dark"]
.salary-input {
  color: #f0f1f3;
}


html[data-theme="dark"]
.currency-wrapper {
  color: #8cb7ff;

  background:
    rgba(70,125,225,0.11);

  border-color:
    rgba(95,150,240,0.13);
}


html[data-theme="dark"]
.currency-dropdown {
  background: #25272c;

  border-color: #3a3d44;

  box-shadow:
    0
    15px
    35px
    rgba(0,0,0,0.35);
}


html[data-theme="dark"]
.currency-dropdown li {
  color: #d8dbe0;
}


html[data-theme="dark"]
.setting-title {
  color: #dedfe3;
}


html[data-theme="dark"]
.setting-description {
  color: #818690;
}


html[data-theme="dark"]
.ios-switch {
  background: #464a52;
}


html[data-theme="dark"]
.cover-skeleton {
  background: #2a2d33;
}


html[data-theme="dark"]
.cover-skeleton::after {
  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255,255,255,0.075),
      transparent
    );
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 700px) {

  .profile-general {
    gap: 13px;
  }


  .identity-card,
  .general-card {
    border-radius: 20px;
  }


  .cover {
    height: 180px;
  }


  .edit-cover-btn {
    right: 12px;
    bottom: 12px;

    width: 38px;

    padding: 0;
  }


  .edit-cover-btn span:not(.cover-spinner) {
    display: none;
  }


  .identity-body {
    align-items: flex-end;

    gap: 12px;

    padding:
      0
      16px
      20px;
  }


  .avatar-wrapper {
    margin-top:
      -40px;
  }


  .avatar-button {
    width: 88px;
    height: 88px;

    border-width: 4px;
  }


  .user-name {
    font-size: 16px;
  }


  .general-card {
    padding:
      21px
      16px;
  }


  .form-grid {
    grid-template-columns:
      1fr;
  }


  .form-field--wide {
    grid-column: auto;
  }


  .save-area {
    align-items: stretch;

    flex-direction: column;

    gap: 12px;
  }


  .save-btn {
    width: 100%;

    min-height: 42px;
  }

}


/* ============================================================
   TRANSITIONS
============================================================ */

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition:
    opacity
    0.18s ease,
    transform
    0.18s ease;
}


.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;

  transform:
    translateY(-5px);
}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .edit-cover-btn,
  .avatar-hover,
  .location-button,
  .form-field input,
  .form-field textarea,
  .status-row,
  .salary-wrapper,
  .save-btn,
  .ios-switch,
  .ios-switch::before {
    transition: none;
  }


  .cover-skeleton::after,
  .cover-spinner,
  .avatar-spinner,
  .button-spinner {
    animation: none;
  }

}

</style>